import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { sendgridApi } from '../api/sendgrid';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../contexts/ToastContext';
import SlideOverPanel from '../components/SlideOverPanel';
import {
  MagnifyingGlassIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ChevronUpDownIcon,
  EyeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { SENDGRID_LISTS, SENDGRID_LIST_NAMES } from '../constants/sendgrid';

const SORT_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'created_at', label: 'Date Added' },
  { value: 'updated_at', label: 'Last Updated' },
];

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function formatRelativeDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

// ── Status badge colors ──

const STATUS_COLORS = {
  subscribed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  unsubscribed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  bounced: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  spam_reported: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  unknown: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

function StatusBadge({ status }) {
  const colorClass = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {status || 'unknown'}
    </span>
  );
}

// ── Event type colors ──

const EVENT_TYPE_COLORS = {
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  open: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  click: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  group_resubscribe: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  deferred: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  processed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  unsubscribe: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  group_unsubscribe: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  bounce: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  spam_report: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  dropped: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

function EventTypeBadge({ type }) {
  const colorClass = EVENT_TYPE_COLORS[type] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {type}
    </span>
  );
}

// ══════════════════════════════════════════════
// Main ContactsPage
// ══════════════════════════════════════════════

function ContactsPage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('local');
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);

  // ── SendGrid tab state ──
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 400);
  const [sortBy, setSortBy] = useState('email');
  const [sortDir, setSortDir] = useState('ASC');
  const [selectedContactIdx, setSelectedContactIdx] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // ── Local tab state ──
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const debouncedLocalSearch = useDebounce(localSearchQuery, 400);
  const [selectedLocalContact, setSelectedLocalContact] = useState(null);
  const [isLocalDetailOpen, setIsLocalDetailOpen] = useState(false);

  // ── SendGrid contacts query ──
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['contactSearch', debouncedSearch, sortBy, sortDir],
    queryFn: () => sendgridApi.searchContacts({
      email: debouncedSearch || undefined,
      sortBy,
      sortDir,
    }),
    enabled: (debouncedSearch.length >= 3 || debouncedSearch.length === 0) && activeTab === 'sendgrid',
  });

  const contacts = searchResults?.result || [];
  const contactCount = searchResults?.contact_count || 0;

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleViewContact = (idx) => {
    setSelectedContactIdx(idx);
    setIsDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
  };

  const handleNavigatePrevious = () => {
    if (selectedContactIdx !== null && selectedContactIdx > 0) {
      setSelectedContactIdx(selectedContactIdx - 1);
    }
  };

  const handleNavigateNext = () => {
    if (selectedContactIdx !== null && selectedContactIdx < contacts.length - 1) {
      setSelectedContactIdx(selectedContactIdx + 1);
    }
  };

  const selectedContact = selectedContactIdx !== null ? contacts[selectedContactIdx] : null;
  const position = selectedContactIdx !== null
    ? { current: selectedContactIdx + 1, total: contacts.length }
    : undefined;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <UsersIcon className="h-7 w-7 text-obn-blue dark:text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Contacts</h1>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'sendgrid' && contactCount > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {contactCount.toLocaleString()} contact{contactCount !== 1 ? 's' : ''}
            </span>
          )}
          <button
            onClick={() => setIsSyncModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Sync
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('local')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'local'
                ? 'border-obn-blue text-obn-blue dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Contacts
          </button>
          <button
            onClick={() => setActiveTab('sendgrid')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'sendgrid'
                ? 'border-obn-blue text-obn-blue dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            SendGrid
          </button>
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'local' ? (
        <LocalContactsView
          searchQuery={localSearchQuery}
          onSearchChange={(e) => setLocalSearchQuery(e.target.value)}
          debouncedSearch={debouncedLocalSearch}
          onViewContact={(contact) => {
            setSelectedLocalContact(contact);
            setIsLocalDetailOpen(true);
          }}
          onOpenSync={() => setIsSyncModalOpen(true)}
        />
      ) : (
        <>
          {/* Search + Sort Bar */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isSearching ? (
                  <svg className="h-5 w-5 text-obn-blue dark:text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                )}
              </div>
              <input
                type="search"
                placeholder="Search by email address..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg text-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
                autoComplete="off"
              />
            </div>
            {debouncedSearch.length > 0 && debouncedSearch.length < 3 && (
              <p className="text-xs text-gray-400 dark:text-gray-500">Type at least 3 characters</p>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                onClick={() => setSortDir((d) => d === 'ASC' ? 'DESC' : 'ASC')}
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5"
                title={`Sort ${sortDir === 'ASC' ? 'ascending' : 'descending'}`}
              >
                <ChevronUpDownIcon className="h-3.5 w-3.5" />
                {sortDir === 'ASC' ? 'A-Z' : 'Z-A'}
              </button>
            </div>
          </div>

          {/* Table */}
          {(debouncedSearch.length >= 3 || debouncedSearch.length === 0) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">List</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date Added</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date Updated</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {contacts.length === 0 && !isSearching ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No contacts found
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact, idx) => {
                      const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
                      const isOnObnList = contact.list_ids?.includes(SENDGRID_LISTS[0].id);

                      return (
                        <tr key={contact.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-4 py-3 text-sm text-obn-blue dark:text-blue-400 font-medium truncate max-w-xs">
                            {contact.email}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {name || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {isOnObnList && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">
                                Off-by-none
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(contact.created_at)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(contact.updated_at)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleViewContact(idx)}
                              className="inline-flex items-center gap-1 text-xs text-obn-blue dark:text-blue-400 hover:text-obn-blue/80 dark:hover:text-blue-300 font-medium"
                            >
                              <EyeIcon className="h-4 w-4" />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* SendGrid detail slide-over */}
          <SlideOverPanel
            isOpen={isDetailOpen}
            onClose={handleCloseDetail}
            title="Contact Details"
            width="50vw"
            onNavigatePrevious={handleNavigatePrevious}
            onNavigateNext={handleNavigateNext}
            position={position}
          >
            {selectedContact && (
              <ContactDetailPanel
                contact={selectedContact}
                queryClient={queryClient}
                showSuccess={showSuccess}
                showError={showError}
              />
            )}
          </SlideOverPanel>
        </>
      )}

      {/* Contact detail slide-over */}
      <SlideOverPanel
        isOpen={isLocalDetailOpen}
        onClose={() => setIsLocalDetailOpen(false)}
        title="Contact Details"
        width="50vw"
      >
        {selectedLocalContact && (
          <LocalContactDetailPanel contact={selectedLocalContact} />
        )}
      </SlideOverPanel>

      {/* Sync modal */}
      {isSyncModalOpen && (
        <SyncModal
          onClose={() => setIsSyncModalOpen(false)}
          showSuccess={showSuccess}
          showError={showError}
          queryClient={queryClient}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════
// LocalContactsView
// ══════════════════════════════════════════════

function LocalContactsView({ searchQuery, onSearchChange, debouncedSearch, onViewContact, onOpenSync }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [sortDir, setSortDir] = useState('newest');
  const isSearchMode = debouncedSearch.length >= 2;

  // Search query
  const { data: searchData, isLoading: isSearchingLocal } = useQuery({
    queryKey: ['localContactSearch', debouncedSearch],
    queryFn: () => sendgridApi.searchLocalContacts(debouncedSearch),
    enabled: isSearchMode,
  });

  // List query (infinite)
  const {
    data: listData,
    isLoading: isListLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['localContacts', statusFilter, sortDir],
    queryFn: ({ pageParam }) => sendgridApi.listLocalContacts({ limit: 50, cursor: pageParam, status: statusFilter || undefined, sort: sortDir }),
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.cursor : undefined,
    initialPageParam: undefined,
    enabled: !isSearchMode,
  });

  const isLoading = isSearchMode ? isSearchingLocal : isListLoading;

  // Flatten paginated results
  const localContacts = isSearchMode
    ? (searchData?.contacts || [])
    : (listData?.pages?.flatMap((p) => p.contacts) || []);

  if (!isLoading && !isSearchMode && localContacts.length === 0) {
    return (
      <div>
        {/* Search bar (always visible) */}
        <div className="relative max-w-md mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            type="search"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={onSearchChange}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg text-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
            autoComplete="off"
          />
        </div>

        {/* Empty state */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <UsersIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No contacts yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Run an initial sync to import contacts from SendGrid.
          </p>
          <button
            onClick={onOpenSync}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-obn-blue rounded-lg hover:bg-obn-blue/90 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Open Sync Manager
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Search bar + filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative max-w-md flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isLoading ? (
              <svg className="h-5 w-5 text-obn-blue dark:text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            )}
          </div>
          <input
            type="search"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={onSearchChange}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg text-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
            autoComplete="off"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
        >
          <option value="">All statuses</option>
          <option value="subscribed">Subscribed</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="bounced">Bounced</option>
          <option value="spam_reported">Spam Reported</option>
        </select>
        <button
          onClick={() => setSortDir((d) => d === 'newest' ? 'oldest' : 'newest')}
          className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 whitespace-nowrap"
          title={`Sorted by ${sortDir === 'newest' ? 'newest first' : 'oldest first'}`}
        >
          <ChevronUpDownIcon className="h-4 w-4" />
          {sortDir === 'newest' ? 'Newest' : 'Oldest'}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Engagement</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ unsubscribed: 'Unsubscribed', bounced: 'Bounced', spam_reported: 'Spam Reported' }[statusFilter] || 'Subscribed'}
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
            {localContacts.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No contacts found
                </td>
              </tr>
            ) : (
              localContacts.map((contact) => {
                const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
                const eng = contact.engagement || {};
                const engParts = [];
                if (eng.totalDelivered) engParts.push(`${eng.totalDelivered} del`);
                if (eng.totalOpens) engParts.push(`${eng.totalOpens} opens`);
                if (eng.totalClicks) engParts.push(`${eng.totalClicks} clicks`);
                const engText = engParts.length > 0 ? engParts.join(' / ') : '-';

                return (
                  <tr key={contact.email} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-4 py-3 text-sm text-obn-blue dark:text-blue-400 font-medium truncate max-w-xs">
                      {contact.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {name || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <StatusBadge status={contact.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {engText}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {(() => {
                        if (statusFilter === 'unsubscribed') {
                          const dateVal = contact.suppression?.unsubscribedAt || contact.suppression?.globalUnsubscribedAt || contact.sync?.sgUpdatedAt;
                          return dateVal ? formatDate(dateVal) : '-';
                        }
                        if (statusFilter === 'bounced') {
                          return contact.suppression?.bouncedAt ? formatDate(contact.suppression.bouncedAt) : '-';
                        }
                        if (statusFilter === 'spam_reported') {
                          return contact.suppression?.spamReportedAt ? formatDate(contact.suppression.spamReportedAt) : '-';
                        }
                        return contact.sync?.sgCreatedAt ? formatDate(contact.sync.sgCreatedAt) : '-';
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => onViewContact(contact)}
                        className="inline-flex items-center gap-1 text-xs text-obn-blue dark:text-blue-400 hover:text-obn-blue/80 dark:hover:text-blue-300 font-medium"
                      >
                        <EyeIcon className="h-4 w-4" />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {!isSearchMode && hasNextPage && (
        <div className="flex justify-center mt-4">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {isFetchingNextPage ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════
// LocalContactDetailPanel
// ══════════════════════════════════════════════

function LocalContactDetailPanel({ contact }) {
  const [eventsCursor, setEventsCursor] = useState(null);

  // Events query
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['localContactEvents', contact.email, eventsCursor],
    queryFn: () => sendgridApi.getLocalContactEvents(contact.email, { limit: 25, cursor: eventsCursor }),
    enabled: !!contact.email,
  });

  const events = eventsData?.events || [];
  const eng = contact.engagement || {};
  const sup = contact.suppression || {};

  return (
    <div className="p-6 space-y-4">
      {/* Contact Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Contact Info</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Email</label>
            <p className="text-sm text-gray-900 dark:text-gray-100">{contact.email}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
            <div className="mt-0.5">
              <StatusBadge status={contact.status} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">First Name</label>
            <p className="text-sm text-gray-900 dark:text-gray-100">{contact.firstName || '-'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Last Name</label>
            <p className="text-sm text-gray-900 dark:text-gray-100">{contact.lastName || '-'}</p>
          </div>
        </div>

        {/* Custom fields */}
        {contact.customFields && Object.keys(contact.customFields).length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Custom Fields</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(contact.customFields).map(([key, value]) => (
                <div key={key}>
                  <label className="text-xs text-gray-400 dark:text-gray-500">{key}</label>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{value ?? '-'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lists */}
        {contact.listIds && contact.listIds.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Lists</h3>
            <div className="flex flex-wrap gap-1">
              {contact.listIds.map((lid) => (
                <span
                  key={lid}
                  className={`inline-block px-2 py-0.5 text-xs rounded ${
                    SENDGRID_LIST_NAMES[lid] ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {SENDGRID_LIST_NAMES[lid] || lid}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Sync metadata */}
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Sync Info</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
            <div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Last Synced:</span>{' '}
              {formatRelativeDate(contact.sync?.lastSyncedAt)}
            </div>
            {contact.sync?.lastSyncSource && (
              <div>
                <span className="text-xs text-gray-400 dark:text-gray-500">Source:</span>{' '}
                {contact.sync.lastSyncSource}
              </div>
            )}
            {contact.sync?.sgCreatedAt && (
              <div>
                <span className="text-xs text-gray-400 dark:text-gray-500">SG Created:</span>{' '}
                {formatDate(contact.sync.sgCreatedAt)}
              </div>
            )}
            {contact.sync?.sgUpdatedAt && (
              <div>
                <span className="text-xs text-gray-400 dark:text-gray-500">SG Updated:</span>{' '}
                {formatDate(contact.sync.sgUpdatedAt)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Engagement Summary Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Engagement Summary</h2>
        {eng.totalDelivered || eng.totalOpens || eng.totalClicks ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{eng.totalDelivered || 0}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{eng.totalOpens || 0}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Opens</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-obn-blue dark:text-blue-400">{eng.totalClicks || 0}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Clicks</div>
              </div>
            </div>
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-300">
              {eng.lastDeliveredAt && (
                <div>
                  <span className="text-xs text-gray-400 dark:text-gray-500">Last Delivered:</span>{' '}
                  {formatRelativeDate(eng.lastDeliveredAt)}
                </div>
              )}
              {eng.lastOpenedAt && (
                <div>
                  <span className="text-xs text-gray-400 dark:text-gray-500">Last Opened:</span>{' '}
                  {formatRelativeDate(eng.lastOpenedAt)}
                </div>
              )}
              {eng.lastClickedAt && (
                <div>
                  <span className="text-xs text-gray-400 dark:text-gray-500">Last Clicked:</span>{' '}
                  {formatRelativeDate(eng.lastClickedAt)}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400">No engagement data available</div>
        )}
      </div>

      {/* Suppression Status Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Suppression Status</h2>
        {sup.bounced || sup.blocked || sup.invalidEmail || sup.unsubscribed || sup.globalUnsubscribed || sup.spamReported || sup.suppressionGroups?.length > 0 ? (
          <div className="space-y-2">
            {sup.bounced && (
              <div className="flex items-start gap-2 p-2 rounded border bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/50">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Bounced</span>
                  {sup.bouncedAt && (
                    <span className="text-xs ml-2">since {formatDate(sup.bouncedAt)}</span>
                  )}
                  {sup.bounceReason && (
                    <p className="text-xs mt-0.5">{sup.bounceReason}</p>
                  )}
                </div>
              </div>
            )}
            {sup.blocked && (
              <div className="flex items-start gap-2 p-2 rounded border bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/50">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Blocked</span>
                  {sup.blockedAt && (
                    <span className="text-xs ml-2">since {formatDate(sup.blockedAt)}</span>
                  )}
                </div>
              </div>
            )}
            {sup.invalidEmail && (
              <div className="flex items-start gap-2 p-2 rounded border bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/50">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Invalid Email</span>
                  {sup.invalidEmailAt && (
                    <span className="text-xs ml-2">since {formatDate(sup.invalidEmailAt)}</span>
                  )}
                </div>
              </div>
            )}
            {sup.unsubscribed && (
              <div className="flex items-start gap-2 p-2 rounded border bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-800/50">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Unsubscribed</span>
                  {sup.unsubscribedAt && (
                    <span className="text-xs ml-2">since {formatDate(sup.unsubscribedAt)}</span>
                  )}
                </div>
              </div>
            )}
            {sup.globalUnsubscribed && (
              <div className="flex items-start gap-2 p-2 rounded border bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-800/50">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Global Unsubscribe</span>
                  {sup.globalUnsubscribedAt && (
                    <span className="text-xs ml-2">since {formatDate(sup.globalUnsubscribedAt)}</span>
                  )}
                </div>
              </div>
            )}
            {sup.spamReported && (
              <div className="flex items-start gap-2 p-2 rounded border bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/50">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Spam Report</span>
                  {sup.spamReportedAt && (
                    <span className="text-xs ml-2">since {formatDate(sup.spamReportedAt)}</span>
                  )}
                </div>
              </div>
            )}
            {sup.suppressionGroups?.length > 0 && (
              <div className="flex items-start gap-2 p-2 rounded border bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-800/50">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Group Suppressions</span>
                  <p className="text-xs mt-0.5">Groups: {sup.suppressionGroups.join(', ')}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <CheckIcon className="h-4 w-4" />
            No suppression issues
          </div>
        )}
      </div>

      {/* Event History Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Event History</h2>
        {eventsLoading ? (
          <div className="text-sm text-gray-400 dark:text-gray-500">Loading events...</div>
        ) : events.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {events.map((event, idx) => {
              const detail = event.reason || event.response || event.status || null;
              const campaign = event.campaignName || event.singlesendName || null;
              return (
                <div key={idx} className="py-2.5 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <EventTypeBadge type={event.eventType} />
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                      {event.timestamp ? new Date(event.timestamp).toLocaleString() : '-'}
                    </span>
                    {campaign && (
                      <span className="text-sm text-gray-700 dark:text-gray-200 font-medium truncate">{campaign}</span>
                    )}
                    {!campaign && event.subject && (
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{event.subject}</span>
                    )}
                  </div>
                  {(event.url || detail) && (
                    <div className="ml-[4.5rem] mt-1 flex flex-col gap-0.5">
                      {event.url && (
                        <a
                          href={event.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-obn-blue dark:text-blue-400 hover:underline truncate"
                          title={event.url}
                        >
                          {event.url.length > 100 ? event.url.slice(0, 100) + '...' : event.url}
                        </a>
                      )}
                      {detail && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">{detail}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Load more events */}
            {eventsData?.hasMore && (
              <div className="pt-3 text-center">
                <button
                  onClick={() => setEventsCursor(eventsData.cursor)}
                  className="text-xs text-obn-blue dark:text-blue-400 hover:text-obn-blue/80 dark:hover:text-blue-300 font-medium"
                >
                  Load More Events
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400">No events recorded</div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// SyncModal
// ══════════════════════════════════════════════

function SyncModal({ onClose, showSuccess, showError, queryClient }) {
  return (
    <div className="fixed inset-0 z-40 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:ring-1 dark:ring-white/10 w-full max-w-lg p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Sync Management</h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <InitialSyncCard showSuccess={showSuccess} showError={showError} queryClient={queryClient} />
          <QuickSyncCard showSuccess={showSuccess} showError={showError} queryClient={queryClient} />
          <SuppressionSyncCard showSuccess={showSuccess} showError={showError} queryClient={queryClient} />
        </div>
      </div>
    </div>
  );
}

// ── Initial Sync Card ──

function InitialSyncCard({ showSuccess, showError, queryClient }) {
  // State machine: idle → exporting → polling → ready → importing → complete → failed
  const [syncState, setSyncState] = useState({ step: 'idle', jobId: null, contactCount: null, result: null, error: null });
  const [selectedListId, setSelectedListId] = useState('');

  // Start export
  const exportMutation = useMutation({
    mutationFn: () => sendgridApi.startInitialSync(selectedListId ? [selectedListId] : undefined),
    onSuccess: (data) => {
      setSyncState({ step: 'polling', jobId: data.jobId, contactCount: null, result: null, error: null });
    },
    onError: (err) => {
      setSyncState((s) => ({ ...s, step: 'failed', error: err.response?.data?.error || 'Failed to start export' }));
    },
  });

  // Poll export status
  const { data: exportStatus } = useQuery({
    queryKey: ['exportStatus', syncState.jobId],
    queryFn: () => sendgridApi.checkExportStatus(syncState.jobId),
    enabled: syncState.step === 'polling' && !!syncState.jobId,
    refetchInterval: 5000,
  });

  // Transition from polling → ready/failed
  useEffect(() => {
    if (!exportStatus || syncState.step !== 'polling') return;
    if (exportStatus.status === 'ready') {
      setSyncState((s) => ({ ...s, step: 'ready', contactCount: exportStatus.contactCount || null }));
    } else if (exportStatus.status === 'failed') {
      setSyncState((s) => ({ ...s, step: 'failed', error: exportStatus.message || 'Export failed' }));
    }
  }, [exportStatus, syncState.step]);

  // Import
  const importMutation = useMutation({
    mutationFn: () => sendgridApi.importExportedContacts(syncState.jobId, {
      sourceListIds: selectedListId ? [selectedListId] : undefined,
    }),
    onSuccess: (data) => {
      setSyncState((s) => ({ ...s, step: 'complete', result: data }));
      queryClient.invalidateQueries({ queryKey: ['localContacts'] });
      showSuccess(`Imported ${data.imported || 0} contacts`);
    },
    onError: (err) => {
      setSyncState((s) => ({ ...s, step: 'failed', error: err.response?.data?.error || 'Import failed' }));
    },
  });

  const handleReset = () => {
    setSyncState({ step: 'idle', jobId: null, contactCount: null, result: null, error: null });
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Initial Sync (Full Export/Import)</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Export all contacts from SendGrid and import into local storage.</p>

      {syncState.step === 'idle' && (
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">List</label>
            <select
              value={selectedListId}
              onChange={(e) => setSelectedListId(e.target.value)}
              className="block text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
            >
              <option value="">All Contacts</option>
              {SENDGRID_LISTS.map((list) => (
                <option key={list.id} value={list.id}>{list.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => {
              setSyncState((s) => ({ ...s, step: 'exporting' }));
              exportMutation.mutate();
            }}
            disabled={exportMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-obn-blue rounded-lg hover:bg-obn-blue/90 disabled:opacity-50 transition-colors"
          >
            Start Export
          </button>
        </div>
      )}

      {(syncState.step === 'exporting' || syncState.step === 'polling') && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <ArrowPathIcon className="h-4 w-4 animate-spin text-obn-blue dark:text-blue-400" />
          {syncState.step === 'exporting' ? 'Starting export...' : 'Export in progress...'}
        </div>
      )}

      {syncState.step === 'ready' && (
        <div className="space-y-2">
          <p className="text-sm text-green-700 dark:text-green-300">
            Export ready.{syncState.contactCount ? ` ${syncState.contactCount.toLocaleString()} contacts.` : ''}
          </p>
          <button
            onClick={() => {
              setSyncState((s) => ({ ...s, step: 'importing' }));
              importMutation.mutate();
            }}
            disabled={importMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            Import Now
          </button>
        </div>
      )}

      {syncState.step === 'importing' && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <ArrowPathIcon className="h-4 w-4 animate-spin text-green-600 dark:text-green-400" />
          Importing contacts...
        </div>
      )}

      {syncState.step === 'complete' && (
        <div className="space-y-2">
          <p className="text-sm text-green-700 dark:text-green-300">
            {syncState.result?.imported || 0} imported
            {syncState.result?.errors ? `, ${syncState.result.errors} errors` : ''}
          </p>
          <button
            onClick={handleReset}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            Run Again
          </button>
        </div>
      )}

      {syncState.step === 'failed' && (
        <div className="space-y-2">
          <p className="text-sm text-red-600 dark:text-red-400">{syncState.error}</p>
          <button
            onClick={handleReset}
            className="text-xs text-obn-blue dark:text-blue-400 hover:text-obn-blue/80 dark:hover:text-blue-300 font-medium"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}

// ── Quick Sync Card ──

function QuickSyncCard({ showSuccess, showError, queryClient }) {
  const quickSyncMutation = useMutation({
    mutationFn: () => sendgridApi.runQuickSync(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['localContacts'] });
      showSuccess(`Synced ${data.synced || 0} contacts`);
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Quick sync failed');
    },
  });

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Quick Sync</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Sync recently modified contacts from SendGrid.</p>
      <button
        onClick={() => quickSyncMutation.mutate()}
        disabled={quickSyncMutation.isPending}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
      >
        {quickSyncMutation.isPending ? (
          <>
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            Syncing...
          </>
        ) : (
          <>
            <ArrowPathIcon className="h-4 w-4" />
            Run Quick Sync
          </>
        )}
      </button>
    </div>
  );
}

// ── Suppression Sync Card ──

function SuppressionSyncCard({ showSuccess, showError, queryClient }) {
  const suppressionMutation = useMutation({
    mutationFn: () => sendgridApi.syncSuppressions(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['localContacts'] });
      showSuccess(data.message || 'Suppression sync started');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Suppression sync failed');
    },
  });

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Suppression Sync</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Bulk sync suppression status from SendGrid (runs in background).</p>
      <button
        onClick={() => suppressionMutation.mutate()}
        disabled={suppressionMutation.isPending}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
      >
        {suppressionMutation.isPending ? (
          <>
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            Starting...
          </>
        ) : (
          <>
            <ArrowPathIcon className="h-4 w-4" />
            Sync Suppressions
          </>
        )}
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════
// SendGrid ContactDetailPanel (existing)
// ══════════════════════════════════════════════

function ContactDetailPanel({ contact, queryClient, showSuccess, showError }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Contact details (full record)
  const { data: contactDetail } = useQuery({
    queryKey: ['contact', contact?.id],
    queryFn: () => sendgridApi.getContact(contact.id),
    enabled: !!contact?.id,
  });

  // Suppression status
  const { data: suppression, isLoading: suppressionLoading } = useQuery({
    queryKey: ['suppression', contact?.email],
    queryFn: () => sendgridApi.getSuppression(contact.email),
    enabled: !!contact?.email,
  });

  // Engagement data
  const { data: engagement, isLoading: engagementLoading } = useQuery({
    queryKey: ['engagement', contact?.email],
    queryFn: () => sendgridApi.getEngagement(contact.email),
    enabled: !!contact?.email,
  });

  // Field definitions for custom fields
  const { data: fieldDefs } = useQuery({
    queryKey: ['fieldDefinitions'],
    queryFn: sendgridApi.getFieldDefinitions,
    staleTime: 1000 * 60 * 30,
  });

  // Update contact mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => sendgridApi.updateContact(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', contact?.id] });
      queryClient.invalidateQueries({ queryKey: ['contactSearch'] });
      setIsEditing(false);
      showSuccess('Contact update queued');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to update contact');
    },
  });

  const handleEdit = () => {
    const c = contactDetail || contact;
    setEditForm({
      first_name: c.first_name || '',
      last_name: c.last_name || '',
    });
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    updateMutation.mutate({ id: contact.id, updates: editForm });
  };

  const hasSuppressionIssues = suppression &&
    (suppression.bounces.length > 0 ||
      suppression.unsubscribes.length > 0 ||
      suppression.spamReports.length > 0);

  // Build custom field labels map
  const customFieldMap = {};
  if (fieldDefs?.custom_fields) {
    fieldDefs.custom_fields.forEach((f) => {
      customFieldMap[f.id] = f.name;
    });
  }

  const displayContact = contactDetail || contact;

  return (
    <div className="p-6 space-y-4">
      {/* Contact info card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Contact Info</h2>
          {!isEditing ? (
            <button
              onClick={handleEdit}
              className="flex items-center gap-1 text-sm text-obn-blue dark:text-blue-400 hover:text-obn-blue/80 dark:hover:text-blue-300"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleSaveEdit}
                disabled={updateMutation.isPending}
                className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
              >
                <CheckIcon className="h-4 w-4" />
                Save
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-1 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-4 w-4" />
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Email</label>
            <p className="text-sm text-gray-900 dark:text-gray-100">{contact.email}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">First Name</label>
            {isEditing ? (
              <input
                type="text"
                value={editForm.first_name}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                className="block w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-gray-100">{displayContact.first_name || '-'}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Last Name</label>
            {isEditing ? (
              <input
                type="text"
                value={editForm.last_name}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                className="block w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-gray-100">{displayContact.last_name || '-'}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Created</label>
            <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(contact.created_at)}</p>
          </div>
        </div>

        {/* Custom fields */}
        {displayContact.custom_fields &&
          Object.keys(displayContact.custom_fields).length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Custom Fields</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(displayContact.custom_fields).map(
                  ([key, value]) => (
                    <div key={key}>
                      <label className="text-xs text-gray-400 dark:text-gray-500">
                        {customFieldMap[key] || key}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-gray-100">{value ?? '-'}</p>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

        {/* Lists */}
        {contact.list_ids && contact.list_ids.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Lists</h3>
            <div className="flex flex-wrap gap-1">
              {contact.list_ids.map((lid) => (
                <span
                  key={lid}
                  className={`inline-block px-2 py-0.5 text-xs rounded ${
                    SENDGRID_LIST_NAMES[lid]
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {SENDGRID_LIST_NAMES[lid] || lid}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Suppression status */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Suppression Status</h2>
        {suppressionLoading ? (
          <div className="text-sm text-gray-400 dark:text-gray-500">Loading...</div>
        ) : hasSuppressionIssues ? (
          <div className="space-y-2">
            {suppression.bounces.length > 0 && (
              <SuppressionBadge type="Bounced" color="red" items={suppression.bounces} />
            )}
            {suppression.unsubscribes.length > 0 && (
              <SuppressionBadge type="Unsubscribed" color="yellow" items={suppression.unsubscribes} />
            )}
            {suppression.spamReports.length > 0 && (
              <SuppressionBadge type="Spam Report" color="red" items={suppression.spamReports} />
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <CheckIcon className="h-4 w-4" />
            No suppression issues
          </div>
        )}
      </div>

      {/* Engagement / Issues Read */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Issue Engagement</h2>
        {engagementLoading ? (
          <div className="text-sm text-gray-400 dark:text-gray-500">Loading...</div>
        ) : engagement?.issuesRead?.length > 0 ? (
          <div className="space-y-2">
            {engagement.issuesRead.map((issue, idx) => (
              <div key={idx} className="flex items-center justify-between py-1.5 border-b border-gray-50 dark:border-gray-700 last:border-0">
                <div className="min-w-0">
                  <span className="text-sm font-medium text-obn-blue dark:text-blue-400">#{issue.issueNumber}</span>
                  <span className="text-sm text-gray-600 dark:text-gray-300 ml-2 truncate">{issue.subject}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                  {issue.clickCount > 0 && (
                    <span>{issue.clickCount} click{issue.clickCount !== 1 ? 's' : ''}</span>
                  )}
                  {issue.openedAt && (
                    <span>{new Date(issue.openedAt).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : engagement?.messages?.length > 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {engagement.messages.length} email{engagement.messages.length !== 1 ? 's' : ''} found, but no opens tracked.
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400">No engagement data available</div>
        )}
      </div>
    </div>
  );
}

function SuppressionBadge({ type, color, items }) {
  const colorClasses = {
    red: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/50',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-800/50',
  };

  return (
    <div className={`flex items-start gap-2 p-2 rounded border ${colorClasses[color]}`}>
      <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <div>
        <span className="text-sm font-medium">{type}</span>
        {items[0]?.created && (
          <span className="text-xs ml-2">
            since {new Date(items[0].created * 1000).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

export default ContactsPage;
